/* eslint-disable react-refresh/only-export-components */
import { createContext, useState, useCallback } from 'react';
import AppIcon from '../components/AppIcon';

export const ToastContext = createContext();

export const ToastProvider = ({ children }) => {
  const [toasts, setToasts] = useState([]);

  const removeToast = useCallback(id => {
    setToasts(prev => prev.filter(toast => toast.id !== id));
  }, []);

  const addToast = useCallback(
    (message, type = 'info', duration = 5000) => {
      const id = Date.now() + Math.random();
      const toast = { id, message, type, duration };

      setToasts(prev => [...prev, toast]);

      if (duration > 0) {
        setTimeout(() => {
          removeToast(id);
        }, duration);
      }

      return id;
    },
    [removeToast]
  );

  const success = useCallback(
    (message, duration) => {
      return addToast(message, 'success', duration);
    },
    [addToast]
  );

  const error = useCallback(
    (message, duration) => {
      return addToast(message, 'error', duration);
    },
    [addToast]
  );

  const warning = useCallback(
    (message, duration) => {
      return addToast(message, 'warning', duration);
    },
    [addToast]
  );

  const info = useCallback(
    (message, duration) => {
      return addToast(message, 'info', duration);
    },
    [addToast]
  );

  const value = {
    toasts,
    addToast,
    removeToast,
    success,
    error,
    warning,
    info,
  };

  return (
    <ToastContext.Provider value={value}>
      {children}
      <ToastContainer toasts={toasts} onRemove={removeToast} />
    </ToastContext.Provider>
  );
};

const ToastContainer = ({ toasts, onRemove }) => {
  if (toasts.length === 0) return null;

  return (
    <div className="fixed top-4 right-4 z-50 space-y-2 pointer-events-none">
      {toasts.map(toast => (
        <Toast key={toast.id} toast={toast} onRemove={onRemove} />
      ))}
    </div>
  );
};

const Toast = ({ toast, onRemove }) => {
  const typeConfig = {
    success: {
      bg: 'bg-green-500',
      icon: 'CheckCircle',
      iconColor: 'text-white',
    },
    error: {
      bg: 'bg-red-500',
      icon: 'AlertCircle',
      iconColor: 'text-white',
    },
    warning: {
      bg: 'bg-yellow-500',
      icon: 'AlertTriangle',
      iconColor: 'text-white',
    },
    info: {
      bg: 'bg-blue-500',
      icon: 'Info',
      iconColor: 'text-white',
    },
  };

  const config = typeConfig[toast.type] || typeConfig.info;

  return (
    <div
      className={`${config.bg} text-white px-4 py-3 rounded-lg shadow-lg flex items-center space-x-3 min-w-[300px] max-w-md pointer-events-auto animate-slide-in`}
    >
      <AppIcon name={config.icon} size={20} className={config.iconColor} />
      <p className="flex-1 text-sm font-medium">{toast.message}</p>
      <button
        onClick={() => onRemove(toast.id)}
        className="text-white hover:text-gray-200 transition-colors"
      >
        <AppIcon name="X" size={16} />
      </button>
    </div>
  );
};
